/** RCA / PA / CA from complaint row and `complaint_comments_details`. */

import { complaintRowStatusKey, normalizeComplaintStatusKey } from './complaintStatus';

export function complaintCommentsFromRaw(raw) {
  const list = raw?.complaint_comments_details;
  if (!Array.isArray(list)) return [];
  return [...list].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return ta - tb;
  });
}

export function formatRcaPaCaField(v) {
  if (v == null) return null;
  if (typeof v === 'string') {
    const t = v.trim();
    return t || null;
  }
  if (typeof v === 'object') {
    if (v.text != null && String(v.text).trim()) return String(v.text).trim();
    if (v.name != null && String(v.name).trim()) return String(v.name).trim();
    if (v.label != null && String(v.label).trim()) return String(v.label).trim();
    if (v.status != null && String(v.status).trim()) return String(v.status).trim();
  }
  const s = String(v).trim();
  return s || null;
}

/** Latest non-null RCA / PA / CA (chronological comments, then row-level fields). */
export function latestRcaPaCaFromRaw(raw) {
  let rca = formatRcaPaCaField(raw?.rca);
  let pa = formatRcaPaCaField(raw?.pa);
  let ca = formatRcaPaCaField(raw?.ca);

  for (const c of complaintCommentsFromRaw(raw)) {
    const nextRca = formatRcaPaCaField(c?.rca);
    const nextPa = formatRcaPaCaField(c?.pa);
    const nextCa = formatRcaPaCaField(c?.ca);
    if (nextRca != null) rca = nextRca;
    if (nextPa != null) pa = nextPa;
    if (nextCa != null) ca = nextCa;
  }

  return { rca: rca ?? '', pa: pa ?? '', ca: ca ?? '' };
}

function latestTransferredComment(raw) {
  const list = raw?.complaint_comments_details;
  if (!Array.isArray(list) || list.length === 0) return null;
  const sorted = [...list].sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
  return sorted.find((c) => normalizeComplaintStatusKey(c?.status) === 'transferred') ?? null;
}

/** Latest transfer target department id from comments or complaint row fields. */
export function latestTransferredToDepartmentId(raw) {
  if (!raw || typeof raw !== 'object') return '';

  const explicit =
    raw.transferred_to_department_id ??
    raw.transferred_to_accountable_department_id ??
    raw.to_accountable_department_id ??
    raw.to_department_id;
  if (explicit != null && String(explicit).trim() !== '') {
    return String(explicit).trim();
  }

  const transferComment = latestTransferredComment(raw);
  if (transferComment) {
    const id =
      transferComment.accountable_department_id ??
      transferComment.to_accountable_department_id ??
      transferComment.to_department_id;
    if (id != null && String(id).trim() !== '') return String(id).trim();
  }

  if (complaintRowStatusKey(raw) === 'transferred') {
    const current = raw.accountable_department_id ?? raw.department_id;
    if (current != null && String(current).trim() !== '') return String(current).trim();
  }
  return '';
}

/** Display name for latest transfer target department, if known. */
export function latestTransferredToDepartmentName(raw) {
  if (!raw || typeof raw !== 'object') return '';
  const explicit =
    raw.transferred_to_department_name ??
    raw.to_department_name ??
    raw.to_accountable_department_name;
  if (explicit != null && String(explicit).trim() !== '') return String(explicit).trim();

  const c = latestTransferredComment(raw);
  const fromComment =
    c?.accountable_department_name ??
    c?.to_department_name ??
    c?.to_accountable_department_name;
  if (fromComment != null && String(fromComment).trim() !== '') return String(fromComment).trim();

  if (complaintRowStatusKey(raw) === 'transferred') {
    const name = raw.accountable_department_name;
    if (name != null && String(name).trim() !== '') return String(name).trim();
  }
  return '';
}

/** Department the complaint transferred from (cannot select as To Department). */
export function transferSourceDepartmentId(raw) {
  if (!raw || typeof raw !== 'object') return '';

  const savedTo = latestTransferredToDepartmentId(raw);
  const fromExplicit =
    raw.transferred_from_accountable_department_id ??
    raw.transferred_from_department_id ??
    raw.from_accountable_department_id ??
    raw.from_department_id ??
    raw.previous_accountable_department_id;
  if (fromExplicit != null && String(fromExplicit).trim() !== '') {
    return String(fromExplicit).trim();
  }

  const current = raw.accountable_department_id ?? raw.department_id;
  const currentKey = current != null && String(current).trim() !== '' ? String(current).trim() : '';
  if (savedTo && currentKey && currentKey === savedTo) return '';
  return currentKey;
}
